import greekArticleBodies from "@/i18n/elArticles";
import { getSiteLocale, localizedApiHref } from "@/i18n/locale";
import { localizeContent, translateHtml, uiText } from "@/i18n/translations";

export type BlogPresentationLink = { label: string; href: string };

export type BlogPresentation = {
  archive_title: string;
  archive_kicker: string;
  archive_kicker_copy: string;
  archive_intro: string;
  filter_label: string;
  all_label: string;
  search_label: string;
  search_placeholder: string;
  unavailable_title: string;
  retry_message: string;
  loading_label: string;
  empty_title: string;
  empty_message: string;
  journal_label: string;
  article_unavailable_title: string;
  return_label: string;
  article_loading_label: string;
  minutes_label: string;
  minutes_read_label: string;
  contents_label: string;
  written_by_label: string;
  more_label: string;
  progress_label: string;
  time_label: string;
  published_label: string;
  read_next_label: string;
  explore_label: string;
  explore_links: Array<{
    title: string;
    meta: string;
    href: string;
    image: {
      image?: string;
      fallback_url?: string;
      alt?: string;
      position?: string;
    };
  }>;
  cta_kicker: string;
  cta_title: string;
  cta_body: string;
  cta_link: BlogPresentationLink;
  services_link: BlogPresentationLink;
  related_eyebrow: string;
  related_title: string;
};

export type BlogJournalSettings = {
  title: string;
  intro: string;
  seoTitle: string;
  seoDescription: string;
  presentation: BlogPresentation;
};

export const DEFAULT_BLOG_JOURNAL: BlogJournalSettings = {
  title: "Blog",
  intro: "Editorial content, insights and SEO-safe articles.",
  seoTitle: "Ideas & Insights",
  seoDescription:
    "Editorial notes, perspective and behind-the-scenes thinking from the VenueX studio.",
  presentation: {
    archive_title: "THE VENUEX JOURNAL",
    archive_kicker: "Journal",
    archive_kicker_copy:
      "Short editorial notes on identity, digital presence, execution and how premium experiences actually hold together.",
    archive_intro:
      "Ideas, insights and perspectives on how strong identities, digital presence and memorable experiences come together to create lasting impact.",
    filter_label: "Filter journal by category",
    all_label: "All",
    search_label: "Search journal",
    search_placeholder: "Search journal",
    unavailable_title: "Journal unavailable.",
    retry_message: "Please try again shortly.",
    loading_label: "Loading journal",
    empty_title: "No stories found.",
    empty_message: "Try another category or search phrase.",
    journal_label: "VenueX journal",
    article_unavailable_title: "Article unavailable.",
    return_label: "Return to the journal",
    article_loading_label: "Loading article",
    minutes_label: "min",
    minutes_read_label: "min read",
    contents_label: "In this guide",
    written_by_label: "Written by",
    more_label: "More from VenueX",
    progress_label: "Reading progress",
    time_label: "Time",
    published_label: "Published",
    read_next_label: "Read next",
    explore_label: "Explore VenueX",
    explore_links: [
      {
        title: "Selected work",
        meta: "Portfolio",
        href: "/portfolio/",
        image: {
          fallback_url: "/static/venuex/live/story-party.jpg",
          alt: "A candlelit VenueX event setting",
          position: "center center",
        },
      },
      {
        title: "What we create",
        meta: "Services",
        href: "/service/",
        image: {
          fallback_url: "/static/venuex/live/venuex-catering.jpg",
          alt: "VenueX event service and hospitality",
          position: "center center",
        },
      },
    ],
    cta_kicker: "Planning an event?",
    cta_title: "Turn the idea into an experience.",
    cta_body:
      "Strategy, creative direction and event production, shaped as one complete journey.",
    cta_link: { label: "Start a project", href: "/contact/" },
    services_link: { label: "Explore VenueX services", href: "/service/" },
    related_eyebrow: "Continue reading",
    related_title: "Related posts",
  },
};

export type BlogPostSummary = {
  id: number;
  slug: string;
  url: string;
  title: string;
  category: string;
  categorySlug: string;
  tags: string[];
  date: string;
  publicationDate: string;
  excerpt: string;
  summary: string;
  heroImage: string;
  heroImageAlt: string;
  featured: boolean;
  authorName: string;
  readingTimeMinutes: number;
};

export type BlogPostDetail = BlogPostSummary & {
  journal: BlogJournalSettings | null;
  seoTitle: string;
  seoDescription: string;
  canonicalUrl: string;
  bodyHtml: string;
  relatedPosts: BlogPostSummary[];
};

export type BlogIndexResponse = {
  journal: BlogJournalSettings | null;
  items: BlogPostSummary[];
  pagination: {
    page: number;
    pages: number;
    perPage: number;
    total: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
};

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? uiText("Article not found.")
        : uiText("The journal could not be loaded."),
    );
  }
  return response.json().then((value) => {
    const localized = localizeContent(value as T);
    if (
      getSiteLocale() === "el" &&
      localized &&
      typeof localized === "object"
    ) {
      const article = localized as Record<string, unknown>;
      const slug = typeof article.slug === "string" ? article.slug : "";
      const body = greekArticleBodies[slug as keyof typeof greekArticleBodies];
      if (body && "bodyHtml" in article)
        article.bodyHtml = translateHtml(body, "el");
    }
    return localized;
  });
}

export async function fetchBlogPosts(signal?: AbortSignal) {
  const response = await fetch(localizedApiHref("/api/blog/?per_page=48"), {
    headers: { Accept: "application/json" },
    signal,
  });
  return parseResponse<BlogIndexResponse>(response);
}

export async function fetchBlogPost(slug: string, signal?: AbortSignal) {
  const response = await fetch(localizedApiHref(`/api/blog/${encodeURIComponent(slug)}/`), {
    headers: { Accept: "application/json" },
    signal,
  });
  return parseResponse<BlogPostDetail>(response);
}
